import React, { useState } from "react";
import { Sparkles, Plus, Edit, Loader2, Trash2, RotateCcw, X, AlertCircle } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";

export function AIDraftClauseDialog({ open, onOpenChange, onDraftGenerated, onAcceptDraft, onEditDraft, onDiscard, aiDrafting, onGenerateDraft, saving }) {
  const [aiPurpose, setAiPurpose] = useState("");
  const [aiJurisdiction, setAiJurisdiction] = useState("");
  const [aiContractType, setAiContractType] = useState("");
  const [aiDraft, setAiDraft] = useState(null);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectFeedback, setRejectFeedback] = useState("");

  const handleGenerate = async () => {
    const result = await onGenerateDraft(aiPurpose, aiJurisdiction, aiContractType, rejectFeedback);
    if (result) {
      setAiDraft(result);
      setRejectMode(false);
      setRejectFeedback("");
    }
  };

  const handleAccept = async () => {
    await onAcceptDraft(aiDraft);
    setAiDraft(null);
    setAiPurpose("");
    setAiJurisdiction("");
    setAiContractType("");
  };

  const handleEdit = () => {
    onEditDraft(aiDraft);
    setAiDraft(null);
    setAiPurpose("");
  };

  const handleDiscard = () => {
    onDiscard();
    setAiDraft(null);
    setAiPurpose("");
    setRejectMode(false);
    setRejectFeedback("");
  };

  const handleClose = (isOpen) => {
    onOpenChange(isOpen);
    if (!isOpen) {
      setAiDraft(null);
      setRejectMode(false);
      setRejectFeedback("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles size={18} className="text-violet-500" />AI Draft Clause</DialogTitle>
          <DialogDescription>
            {aiDraft ? "Review the AI-generated clause. Accept, Edit, or Reject." : "Describe what the clause should cover."}
          </DialogDescription>
        </DialogHeader>

        {!aiDraft ? (
          <>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>What should this clause cover? *</Label>
                <Textarea value={aiPurpose} onChange={(e) => setAiPurpose(e.target.value)} placeholder="e.g., Protect confidential business information..." rows={4} className="rounded-sm text-sm" data-testid="ai-clause-purpose" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Jurisdiction (optional)</Label>
                  <Input value={aiJurisdiction} onChange={(e) => setAiJurisdiction(e.target.value)} placeholder="e.g., England & Wales" className="rounded-sm text-sm" data-testid="ai-clause-jurisdiction" />
                </div>
                <div className="space-y-2">
                  <Label>Contract Type (optional)</Label>
                  <Input value={aiContractType} onChange={(e) => setAiContractType(e.target.value)} placeholder="e.g., Service Agreement" className="rounded-sm text-sm" data-testid="ai-clause-contract-type" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => handleClose(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={handleGenerate} disabled={aiDrafting || !aiPurpose.trim()} className="rounded-sm gap-2 bg-violet-600 hover:bg-violet-700" data-testid="generate-clause-btn">
                {aiDrafting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {aiDrafting ? "Drafting..." : "Generate Clause"}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="space-y-4 py-2">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-semibold text-slate-800">{aiDraft.clause_name}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">{aiDraft.description}</p>
                </div>
                <Badge variant="outline" className="text-xs shrink-0">{aiDraft.category}</Badge>
              </div>
              <div className="bg-slate-50 dark:bg-slate-800/50 border rounded-sm p-4 max-h-64 overflow-y-auto" data-testid="ai-draft-preview">
                <pre className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap font-sans leading-relaxed">{aiDraft.content}</pre>
              </div>
              {aiDraft.is_mandatory && (
                <Badge className="bg-red-100 text-red-700 rounded-sm text-xs"><AlertCircle size={12} className="mr-1" />AI suggests marking as mandatory</Badge>
              )}
            </div>
            <DialogFooter className="flex-col gap-3">
              {!rejectMode ? (
                <div className="flex items-center gap-2 w-full">
                  <Button variant="outline" onClick={() => setRejectMode(true)} className="rounded-sm gap-1.5 border-red-200 text-red-600 hover:bg-red-50" data-testid="reject-draft-btn"><Trash2 size={14} />Reject</Button>
                  <div className="flex gap-2 ml-auto">
                    <Button variant="outline" onClick={handleEdit} className="rounded-sm gap-1.5" data-testid="edit-draft-btn"><Edit size={14} />Edit Before Saving</Button>
                    <Button onClick={handleAccept} disabled={saving} className="rounded-sm gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid="accept-draft-btn">
                      {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}Accept & Save
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="w-full space-y-2 p-3 border border-red-200 bg-red-50/30 rounded-sm" data-testid="reject-feedback-area">
                  <p className="text-xs font-medium text-slate-600">What should be different?</p>
                  <Textarea value={rejectFeedback} onChange={(e) => setRejectFeedback(e.target.value)} placeholder="e.g., Make the liability cap per-incident..." rows={2} className="rounded-sm text-sm" data-testid="reject-feedback-input" />
                  <div className="flex items-center gap-2">
                    <Button onClick={handleGenerate} disabled={aiDrafting || !rejectFeedback.trim()} size="sm" className="rounded-sm gap-1.5 bg-violet-600 hover:bg-violet-700" data-testid="retry-with-feedback-btn">
                      {aiDrafting ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}{aiDrafting ? "Redrafting..." : "Retry with Feedback"}
                    </Button>
                    <Button variant="outline" onClick={handleDiscard} size="sm" className="rounded-sm gap-1.5 border-red-200 text-red-600 hover:bg-red-50" data-testid="discard-draft-btn"><X size={14} />Discard</Button>
                    <Button variant="ghost" onClick={() => setRejectMode(false)} size="sm" className="rounded-sm text-slate-500 ml-auto">Cancel</Button>
                  </div>
                </div>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
